/**
 * VCC button class bundles — one variant per action.
 *
 * @example
 *   <button className={vccBtn('execute')}>Execute</button>
 *   <button className={vccBtn('export', { icon: false })}>Export</button>
 *
 * Prefer shared components from `@shared/components/buttons` when possible.
 */

export type VccButtonVariant =
  | 'execute'
  | 'export'
  | 'reset'
  | 'search'
  | 'save'
  | 'add'
  | 'secondary'
  | 'cancel'
  | 'link'

export type VccBtnOptions = {
  /** Adds vcc-btn-with-icon spacing (default true for icon variants) */
  icon?: boolean
  /** Extra classes appended at the end */
  extra?: string
  /** Include legacy Bootstrap .btn class (default true) */
  bootstrap?: boolean
}

const ICON_VARIANTS: ReadonlySet<VccButtonVariant> = new Set([
  'execute',
  'export',
  'reset',
  'search',
  'save',
  'add',
])

export function vccBtn(variant: VccButtonVariant, options: VccBtnOptions = {}): string {
  const { icon = ICON_VARIANTS.has(variant), extra = '', bootstrap = true } = options

  if (variant === 'link') {
    return ['vcc-btn-link', extra].filter(Boolean).join(' ')
  }

  const parts = [
    bootstrap ? 'btn' : '',
    'vcc-btn',
    `vcc-btn-${variant}`,
    icon ? 'vcc-btn-with-icon' : '',
    extra,
  ]

  return parts.filter(Boolean).join(' ')
}

/** Shorthand map for copy-paste in JSX */
export const VCC_BTN = {
  execute: vccBtn('execute'),
  export: vccBtn('export'),
  reset: vccBtn('reset'),
  search: vccBtn('search'),
  save: vccBtn('save'),
  add: vccBtn('add'),
  secondary: vccBtn('secondary', { icon: false }),
  cancel: vccBtn('cancel', { icon: false }),
  link: vccBtn('link'),
} as const
